/**
 * Crowded buckets (issue #369): how unusually hard each asset-class bucket was
 * bought or sold over a window, measured against ITS OWN history.
 *
 * The definition is the `rs5` feature validated in issue #367
 * (scripts/research/ASSET_BUCKET_STUDY.md): the window return on
 * distribution-adjusted closes, as a z-score over the asset's trailing 252
 * sessions (past data only). Raw returns would just rank risk -- EEM always
 * "moves more" than SHY -- which is the artefact that study removed.
 *
 * WHAT IS NOT SAID, and why. Issue #367 measured one forward-looking pattern -- the week's
 * most-bought groups did slightly worse than the most-sold over the following week (~0.2%, 10 of 11
 * holdout years, only when read at the close) -- and found every "this group is FULL" measure null.
 * The pattern is real but far too small for a reader to use, and explaining it was clutter, so NO
 * surface makes a forward-looking statement. This is a description of where money went and how
 * that compares with the period before. Do not add a prediction here without new evidence.
 *
 * So is HOW IT IS SAID. Every user-facing word -- bucket names, headings, tags, the buying / selling
 * words used in comparisons, the takeaway and the comparison -- is
 * written here once, in plain language, and rendered by both the desktop tab and
 * the mobile Brief card. No sigma, no basis points, no "crowded": a reader who
 * has never seen a z-score should still get it. Change the wording HERE.
 *
 * Pure and dependency-free so a worker can import it cross-tree.
 */

export const Z_WINDOW = 252; // sessions of own history a move is measured against
export const Z_MIN = 126;    // fewer than this and the bucket is omitted, never guessed
export const CALLOUTS = 3;   // top / bottom N called out -- the study's top-3-minus-bottom-3

/**
 * `continues`: in the #367 holdout commodities did NOT revert -- their surges significantly
 * continued (gold leaned the same way, not significantly, so it is not flagged). No surface renders
 * this; it is kept for a future consumer (an AI-portfolio tilt) that would act on the #367 pattern.
 *
 * `kind` is the everyday split a reader already has in their head: where money goes when people
 * are nervous (government bonds, high-grade bonds, the dollar, gold) vs where it goes when they are
 * confident (stocks, junk bonds, real estate, commodities). It feeds `summarizeTilt` only.
 */
export const BUCKETS = [
  { ticker: 'SPY', label: 'Large US stocks', group: 'Stocks', kind: 'riskier' },
  { ticker: 'IWM', label: 'Small US stocks', group: 'Stocks', kind: 'riskier' },
  { ticker: 'EFA', label: 'Overseas developed stocks', group: 'Stocks', kind: 'riskier' },
  { ticker: 'EEM', label: 'Emerging-market stocks', group: 'Stocks', kind: 'riskier' },
  { ticker: 'TLT', label: 'Long-term government bonds', group: 'Government bonds', kind: 'safer' },
  { ticker: 'IEF', label: 'Medium-term government bonds', group: 'Government bonds', kind: 'safer' },
  { ticker: 'SHY', label: 'Short-term government bonds', group: 'Government bonds', kind: 'safer' },
  { ticker: 'LQD', label: 'High-grade corporate bonds', group: 'Corporate bonds', kind: 'safer' },
  { ticker: 'HYG', label: 'High-yield (junk) bonds', group: 'Corporate bonds', kind: 'riskier' },
  { ticker: 'GLD', label: 'Gold', group: 'Real assets', kind: 'safer' },
  { ticker: 'DBC', label: 'Commodities', group: 'Real assets', kind: 'riskier', continues: true },
  { ticker: 'VNQ', label: 'Real estate', group: 'Real assets', kind: 'riskier' },
  { ticker: 'UUP', label: 'US dollar', group: 'Currency', kind: 'safer' },
];

/**
 * A payload's rows re-stamped with THIS file's name / group / kind for each ticker. Surfaces call it
 * instead of trusting the payload's own copies: those were baked into the worker (and its cached
 * row) at deploy time, so a wording change here would otherwise wait on a redeploy.
 */
export const withBucketMeta = (rows) => (rows || []).map((row) => {
  const bucket = BUCKETS.find((b) => b.ticker === row.ticker);
  return bucket ? { ...row, ...bucket, continues: Boolean(bucket.continues) } : row;
});

/** One entry per window the UI offers: its length, and the plain names of it and the window before it. */
export const WINDOWS = [
  { key: '1d', label: '1D', sessions: 1, period: 'the last trading day', before: 'the day before' },
  { key: '1w', label: '1W', sessions: 5, period: 'the last week', before: 'the week before' },
  { key: '1m', label: '1M', sessions: 21, period: 'the last month', before: 'the month before' },
  { key: '3m', label: '3M', sessions: 63, period: 'the last 3 months', before: 'the 3 months before' },
];
/** The window a surface opens on. The week, not the day: it is the one #367 validated and the Brief's frame. */
export const DEFAULT_WINDOW = '1w';

/** Headings and tags for the two sides. `callout` values stay 'crowded' / 'abandoned' on the wire. */
export const SIDES = {
  crowded: { tag: 'most bought', heading: 'Money moved into', lead: 'Bought more than usual' },
  abandoned: { tag: 'most sold', heading: 'Money moved out of', lead: 'Sold more than usual' },
};

/**
 * How long a row's bar is drawn, as a share (0-1) of one side of the track. ONE scale per
 * list, never below 2 so a quiet week does not fill the track. Shared by the desktop tab
 * and the mobile Brief card so the same reading cannot be drawn at two lengths.
 */
export const barScale = (rows) => Math.max(2, ...(rows || []).map((r) => Math.abs(r.z)).filter(Number.isFinite)) * 1.05;
export const barShare = (z, scale) => Math.min(Math.abs(z) / scale, 1);
/** What the two ends of a bar track mean, and why a small move can draw a long bar. */
export const AXIS_ENDS = { sold: 'sold more than usual', bought: 'bought more than usual' };
export const BAR_NOTE = 'A longer bar means a more unusual move for that group, so a small move in bonds can outrank a bigger one in stocks.';

/**
 * The desktop tab's grid (issue #400): every group is a row, every window the payload carries is
 * a column, so a reader sees at a glance whether a lean is a one-day blip or has been building
 * for a quarter. ONE bar scale across every window (a row reads left to right), rows ordered by
 * the chosen window (the one the words describe), a missing reading is `null` — a dash, never a
 * zero-length bar.
 *
 * @param data the `/fear-greed/buckets` payload
 * @param chosenKey the window whose order, callouts and words the view follows
 * @returns null when nothing is offered, else `{ windows, chosen, rows, scale }` where each row
 *   is the chosen window's row (through withBucketMeta) plus `cells: { [windowKey]: row | null }`.
 */
export function windowGrid(data, chosenKey) {
  const windows = WINDOWS.filter((w) => data?.windows?.[w.key]?.rows?.length);
  if (!windows.length) return null;
  const chosen = windows.find((w) => w.key === chosenKey) || windows[0];
  const byWindow = Object.fromEntries(
    windows.map((w) => [w.key, new Map(withBucketMeta(data.windows[w.key].rows).map((r) => [r.ticker, r]))]),
  );
  const rows = [...byWindow[chosen.key].values()].map((row) => ({
    ...row,
    cells: Object.fromEntries(windows.map((w) => [w.key, byWindow[w.key].get(row.ticker) || null])),
  }));
  const scale = barScale(rows.flatMap((r) => Object.values(r.cells).filter(Boolean)));
  return { windows, chosen, rows, scale };
}
export const GRID_NOTE = 'Each bar compares a group\'s move over that window with what is normal for the group, on one scale across every window — so a row shows whether a lean is a one-day blip or has been building, and a small move in short-term government bonds can rank above a large one in stocks. It shows how unusual the move was, not whether a group is "too full".';

/** Below this gap between the two kinds' average scores there is no story to tell. */
export const TILT_THRESHOLD = 0.5;
export const METHOD_NOTE = 'Worked out from price moves, not from actual fund flows.';

/**
 * The one-line takeaway: did money lean toward the safer groups or the riskier ones?
 * DESCRIPTION of the same scores the bars show -- never a forecast. Five outcomes: toward `safety`,
 * toward `risk`, nearly everything `sold` / `bought` (both kinds lean the same way -- this outranks
 * the gap between them), or `mixed` (no gap and no shared lean, or either kind has no bucket placed).
 * @param rows   one window's rows ({ kind, z })
 * @param period the window's plain period name ("the last week")
 */
export function summarizeTilt(rows, period) {
  const of = (kind) => (rows || []).filter((r) => r.kind === kind);
  const safer = of('safer'), riskier = of('riskier');
  const avg = (group) => mean(group.map((r) => r.z));
  const gap = safer.length && riskier.length ? avg(riskier) - avg(safer) : 0;
  // The second sentence COUNTS what happened rather than asserting a tidy story: "toward safety"
  // can be true on average while short-term government bonds were among the most sold.
  const better = (group) => `${group.filter((r) => r.z > 0).length} of ${group.length}`;
  const worse = (group) => `${group.filter((r) => r.z < 0).length} of ${group.length}`;
  const RISKIER = 'riskier groups (stocks, junk bonds, real estate, commodities)';
  const SAFER = 'safer ones (government bonds, high-grade bonds, the dollar, gold)';

  // Both kinds leaning the SAME way is its own story, and it outranks the gap between them: money was
  // arriving or leaving, not rotating. Without this, a week when everything fell but safer groups fell
  // hardest reads as "investors leaned into riskier investments" -- true of the gap, false in plain words.
  const all = [...safer, ...riskier];
  const harder = (word) => (Math.abs(gap) >= TILT_THRESHOLD ? ` ${gap > 0 === (word === 'sold') ? 'Safer' : 'Riskier'} groups were ${word === 'sold' ? 'hit hardest' : 'bought the most'}.` : '');
  if (safer.length && riskier.length) {
    if (avg(safer) <= -TILT_THRESHOLD && avg(riskier) <= -TILT_THRESHOLD) {
      return {
        tilt: 'sold',
        headline: `Almost everything was sold over ${period}`,
        detail: `${worse(all)} groups did worse than is normal for them, safer and riskier alike — money was pulling back across the board rather than moving from one to another.${harder('sold')}`,
      };
    }
    if (avg(safer) >= TILT_THRESHOLD && avg(riskier) >= TILT_THRESHOLD) {
      return {
        tilt: 'bought',
        headline: `Almost everything was bought over ${period}`,
        detail: `${better(all)} groups did better than is normal for them, safer and riskier alike — money was coming in across the board rather than moving from one to another.${harder('bought')}`,
      };
    }
  }
  if (gap >= TILT_THRESHOLD) {
    return {
      tilt: 'risk',
      headline: `Investors leaned into riskier investments over ${period}`,
      detail: `${better(riskier)} ${RISKIER} did better than is normal for them, while ${worse(safer)} ${SAFER} did worse.`,
    };
  }
  if (gap <= -TILT_THRESHOLD) {
    return {
      tilt: 'safety',
      headline: `Investors moved toward safety over ${period}`,
      detail: `${worse(riskier)} ${RISKIER} did worse than is normal for them, while ${better(safer)} ${SAFER} did better.`,
    };
  }
  return {
    tilt: 'mixed',
    headline: `No clear lean between safer and riskier investments over ${period}`,
    detail: safer.length && riskier.length
      ? `${better(riskier)} ${RISKIER} did better than is normal for them, and so did ${better(safer)} ${SAFER} — money shuffled between individual groups rather than toward or away from risk.`
      : 'Not enough groups to compare right now.',
  };
}

/** How each outcome reads inside "...the week before, when ___". */
const TILT_CLAUSES = {
  safety: 'investors moved toward safety',
  risk: 'investors leaned into riskier investments',
  sold: 'almost everything was sold',
  bought: 'almost everything was bought',
  mixed: 'there was no clear lean either way',
};
export const SWING_MIN = 1.5;        // a change in score smaller than this is not worth naming
export const COMPARE_MIN_ROWS = 8;   // fewer groups with a prior reading than this and nothing is compared

/**
 * A score as buying / selling, used ONLY where describing the flow is the content: the comparison
 * with the period before ("from heavy selling to some buying"). Rows deliberately carry NO such
 * qualifier -- a bar already shows how unusual a move is, and words beside every percentage
 * ("strong for it", then "much bigger than usual") were judged noise by the owner. Don't re-add them.
 */
const BANDS = [
  { below: 0.5, flow: 'little change' },
  { below: 1, flow: 'some {dir}' },
  { below: 2, flow: 'heavy {dir}' },
  { below: Infinity, flow: 'very heavy {dir}' },
];

/**
 * A score as a plain phrase about buying or selling: "heavy selling", "some buying", "little change".
 * null for a missing reading -- absence is never described as "little change".
 */
export const flowWords = (z) => {
  if (!Number.isFinite(z)) return null;
  const flow = BANDS.find((band) => Math.abs(z) < band.below).flow;
  return flow.replace('{dir}', z > 0 ? 'buying' : 'selling');
};

/**
 * A per-group note, ONLY for a group whose behaviour changed a lot: "the week before: heavy selling".
 * Sparse on purpose -- a marker on every row (tried: a tick per bar) read as noise, not comparison.
 * @returns {string | null}
 */
export const swingNote = (row, window) => (
  Number.isFinite(row?.prevZ) && Math.abs(row.z - row.prevZ) >= SWING_MIN
    ? `${window.before}: ${flowWords(row.prevZ)}`
    : null
);

/**
 * How this period compares with the one immediately before it (same length, non-overlapping):
 * whether the overall lean held or changed, and the one group whose behaviour swung the most.
 * @returns {string | null} null when too few groups carry a prior reading (`prevZ`).
 */
export function compareToPrevious(rows, window) {
  const withPrior = (rows || []).filter((r) => Number.isFinite(r.prevZ));
  if (withPrior.length < COMPARE_MIN_ROWS) return null;
  const now = summarizeTilt(rows, window.period).tilt;
  const prior = summarizeTilt(withPrior.map((r) => ({ kind: r.kind, z: r.prevZ })), window.before).tilt;
  const lean = now === prior
    // "also" only fits the two clauses whose subject is investors; for the rest the replace is a deliberate no-op
    ? `The same as ${window.before}, when ${TILT_CLAUSES[prior].replace('investors ', 'investors also ')}.`
    : `A change from ${window.before}, when ${TILT_CLAUSES[prior]}.`;
  const swing = [...withPrior].sort((a, b) => Math.abs(b.z - b.prevZ) - Math.abs(a.z - a.prevZ))[0];
  const biggest = Math.abs(swing.z - swing.prevZ) >= SWING_MIN
    ? ` Biggest swing: ${swing.label}, from ${flowWords(swing.prevZ)} to ${flowWords(swing.z)}.`
    : '';
  return lean + biggest;
}

const mean = (xs) => xs.reduce((a, x) => a + x, 0) / xs.length;
const sd = (xs) => {
  const m = mean(xs);
  return Math.sqrt(xs.reduce((a, x) => a + (x - m) ** 2, 0) / (xs.length - 1));
};

/**
 * @param closes distribution-adjusted closes, oldest first
 * @returns {{ movePct, z } | null} the latest `sessions`-session move and its z-score against the
 *   trailing Z_WINDOW of the same move; null when history is too short or flat.
 */
export function crowdingOf(closes, sessions) {
  const moves = [];
  for (let i = sessions; i < closes.length; i += 1) {
    const a = closes[i - sessions], b = closes[i];
    if (!(a > 0) || !(b > 0)) return null; // a hole in the series is not a flat day
    moves.push(b / a - 1);
  }
  const history = moves.slice(-Z_WINDOW);
  if (history.length < Z_MIN) return null;
  const spread = sd(history);
  if (!(spread > 0)) return null;
  const latest = history[history.length - 1];
  return { movePct: latest * 100, z: (latest - mean(history)) / spread };
}

/**
 * @param seriesByTicker { TICKER: [{ date, adjClose }] } oldest first
 * @returns {{ asOf, windows: { [key]: { rows, omitted } } }} rows sorted most-crowded first, each
 *   { ticker, label, group, kind, continues, movePct, z, prevZ, prevMovePct, callout: 'crowded' | 'abandoned' | null }
 *   (`prev*` = the same reading one full window earlier; null when history is too short).
 *   A bucket whose newest bar is not the common `asOf` session is omitted, not compared across dates.
 */
export function buildBucketCrowding(seriesByTicker) {
  const lastDates = BUCKETS.map((b) => seriesByTicker?.[b.ticker]?.at(-1)?.date).filter(Boolean);
  if (!lastDates.length) return { asOf: null, windows: {} };
  const asOf = lastDates.sort().at(-1);

  const windows = {};
  for (const w of WINDOWS) {
    const rows = [];
    const omitted = [];
    for (const bucket of BUCKETS) {
      const series = seriesByTicker[bucket.ticker];
      const closes = series?.at(-1)?.date === asOf ? series.map((p) => p.adjClose) : null;
      const crowding = closes ? crowdingOf(closes, w.sessions) : null;
      // the same reading one full window earlier (non-overlapping), for "compared with the week before"
      const prior = closes ? crowdingOf(closes.slice(0, -w.sessions), w.sessions) : null;
      if (crowding) {
        rows.push({
          ...bucket, continues: Boolean(bucket.continues), ...crowding,
          prevZ: prior ? prior.z : null, prevMovePct: prior ? prior.movePct : null, callout: null,
        });
      }
      else omitted.push(bucket.ticker);
    }
    rows.sort((a, b) => b.z - a.z);
    // a callout needs the right SIGN too: in a week when everything was sold, nothing is "crowded"
    rows.slice(0, CALLOUTS).forEach((r) => { if (r.z > 0) r.callout = 'crowded'; });
    rows.slice(-CALLOUTS).forEach((r) => { if (r.z < 0 && !r.callout) r.callout = 'abandoned'; });
    windows[w.key] = { rows, omitted };
  }
  return { asOf, windows };
}
